import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, Users, MessageSquare, ListChecks, Inbox, Lightbulb,
  BarChart3, Settings as GearIcon, LogOut, Plus,
  Folder, Rss, CheckSquare, ServerCog, ChevronDown, ChevronRight,
  Search, FileText, Hash, ClipboardList,
  CalendarClock, Layers, Images, Share2, Droplets,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { BudgetChip } from "./BudgetChip";

const PRIMARY_NAV = [
  { to: "/",      label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/chat",  label: "Chat",      icon: MessageSquare,   testId: "nav-chat",  badge: "chat" },
  { to: "/floor", label: "Floor",     icon: Users,           testId: "nav-floor" },
];

const NAV_GROUPS = [
  {
    id: "management", label: "Management", icon: ClipboardList,
    children: [
      { to: "/approvals",     label: "Approvals",     icon: CheckSquare, testId: "nav-approvals", badge: "tasks" },
      { to: "/inbound",       label: "Inbound",       icon: Inbox,       testId: "nav-inbound",   badge: "inbound" },
      { to: "/opportunities", label: "Opportunities", icon: Lightbulb,   testId: "nav-opportunities" },
      { to: "/costs",         label: "Costs",         icon: BarChart3,   testId: "nav-costs" },
      { to: "/settings",      label: "Settings",      icon: GearIcon,    testId: "nav-settings" },
    ],
  },
  {
    id: "development", label: "Development", icon: ListChecks,
    children: [
      { to: "/board",      label: "Tasks",      icon: ListChecks, testId: "nav-tasks", badge: "tasks" },
      { to: "/projects",   label: "Projects",   icon: Folder,     testId: "nav-projects" },
      { to: "/feed",       label: "Agent feed", icon: Rss,        testId: "nav-feed" },
      { to: "/monitoring", label: "IT",         icon: ServerCog,  testId: "nav-monitoring" },
    ],
  },
  {
    id: "seo", label: "SEO", icon: Search,
    children: [
      { to: "/seo",          label: "Overview", icon: LayoutDashboard, testId: "nav-seo-overview" },
      { to: "/seo/reports",  label: "Reports",  icon: FileText,        testId: "nav-seo-reports" },
      { to: "/seo/keywords", label: "Keywords", icon: Hash,            testId: "nav-seo-keywords" },
      { to: "/seo/worklog",  label: "Work log", icon: ClipboardList,   testId: "nav-seo-worklog" },
    ],
  },
  {
    id: "marketing", label: "Marketing", icon: Layers,
    children: [
      { to: "/marketing",         label: "Publish queue",  icon: CalendarClock, testId: "nav-mkt-queue" },
      { to: "/marketing/plans",   label: "Plan versions",  icon: Layers,        testId: "nav-mkt-plans" },
      { to: "/marketing/gallery", label: "Visual gallery", icon: Images,        testId: "nav-mkt-gallery" },
    ],
  },
  {
    id: "social", label: "Social", icon: Share2,
    children: [
      { to: "/social", label: "Social Studio", icon: Share2, testId: "nav-social-studio" },
    ],
  },
  {
    id: "afg", label: "AFG Dubai", icon: Droplets,
    children: [
      { to: "/afg", label: "AFG Dubai leads", icon: Droplets, testId: "nav-afg-dubai" },
    ],
  },
];

const LS_OPEN = "office.nav.openGroups";

function loadOpenGroups() {
  try {
    const raw = localStorage.getItem(LS_OPEN);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : null;
  } catch { return null; }
}
function saveOpenGroups(set) {
  try { localStorage.setItem(LS_OPEN, JSON.stringify([...set])); } catch {}
}
function activeGroupId(pathname) {
  // Return the group id whose child's `to` matches best (longest prefix).
  let best = null, bestLen = -1;
  for (const g of NAV_GROUPS) {
    for (const c of g.children) {
      if (pathname === c.to || pathname.startsWith(c.to + "/")) {
        if (c.to.length > bestLen) { best = g.id; bestLen = c.to.length; }
      }
    }
  }
  return best;
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { document.documentElement.classList.add("dark"); }, []);

  const { data: chat } = useQuery({
    queryKey: ["chat-unread"],
    queryFn: async () => (await api.get("/messages/unread_count")).data,
    refetchInterval: 20000, enabled: !!user,
  });
  const { data: inbound } = useQuery({
    queryKey: ["inbound-unread"],
    queryFn: async () => (await api.get("/inbound/unread_count")).data,
    refetchInterval: 20000, enabled: !!user,
  });
  const { data: stats } = useQuery({
    queryKey: ["dashboard-stats-nav"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
    refetchInterval: 20000, enabled: !!user,
  });

  const badgeFor = (b) => {
    if (b === "chat") return chat?.count || 0;
    if (b === "inbound") return inbound?.count || 0;
    if (b === "tasks") return stats?.counts?.pending_approvals || 0;
    return 0;
  };

  // Open-groups state: user toggles + always include the active group.
  const activeGid = activeGroupId(location.pathname);
  const [open, setOpen] = useState(() => {
    const persisted = loadOpenGroups();
    if (persisted) return persisted;
    return new Set(activeGid ? [activeGid] : []);
  });
  useEffect(() => {
    if (activeGid && !open.has(activeGid)) {
      const next = new Set(open); next.add(activeGid);
      setOpen(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGid]);
  useEffect(() => { saveOpenGroups(open); }, [open]);

  const toggle = (gid) => {
    const next = new Set(open);
    next.has(gid) ? next.delete(gid) : next.add(gid);
    setOpen(next);
  };

  const anyOnline = (stats?.agents || []).some((a) => a.online);
  const rate = stats?.usd_per_ecu;
  const pub = stats?.publish_queue || {};

  return (
    <div className="min-h-screen flex font-ui"
         style={{ background: "var(--bg)", color: "var(--fg)" }}>
      <aside
        className="w-[232px] shrink-0 h-screen sticky top-0 flex flex-col"
        style={{ background: "var(--rail)", borderRight: "1px solid var(--line)" }}
        data-testid="app-rail"
      >
        <div className="px-5 pt-6 pb-5 flex items-center gap-2.5">
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <rect x="2" y="8" width="14" height="3" rx="0.5"
                  fill="var(--cobalt)" transform="rotate(-24 9 9.5)" />
          </svg>
          <div className="leading-tight">
            <div className="font-ui font-bold text-[15px] text-[color:var(--fg)]">
              Stackly <span className="text-[color:var(--muted)] font-medium">Office</span>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 overflow-y-auto">
          <ul className="space-y-0.5">
            {PRIMARY_NAV.map((n) => (
              <li key={n.to}><NavItem {...n} count={badgeFor(n.badge)} /></li>
            ))}
          </ul>

          {NAV_GROUPS.map((g) => {
            const isOpen = open.has(g.id);
            const groupBadge = isOpen ? 0 : g.children.reduce((s, c) => s + badgeFor(c.badge), 0);
            return (
              <div key={g.id} className="mt-2">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`navgroup-${g.id}`}
                  onClick={() => toggle(g.id)}
                  data-testid={`nav-group-${g.id}`}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-[6px] text-[11px] font-mono uppercase tracking-[0.1em] text-[color:var(--muted)] hover:text-[color:var(--fg)]"
                >
                  <g.icon className="h-3.5 w-3.5" />
                  <span className="flex-1 text-left">{g.label}</span>
                  {groupBadge > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 text-[9.5px] font-mono rounded-full text-white tabular-nums"
                          style={{ background: "var(--cobalt)" }}
                          data-testid={`nav-group-${g.id}-badge`}>
                      {groupBadge}
                    </span>
                  )}
                  {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </button>
                {isOpen && (
                  <ul id={`navgroup-${g.id}`} className="mt-0.5 ml-2 pl-2 border-l border-[color:var(--line)] space-y-0.5">
                    {g.children.map((n) => (
                      <li key={n.to}><NavItem {...n} count={badgeFor(n.badge)} /></li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}

          <div className="h-px my-3 mx-2 bg-[color:var(--line)]" />
          <button
            onClick={() => navigate("/tasks/new")}
            data-testid="new-task-btn"
            className="w-full flex items-center gap-3 px-2.5 py-2 rounded-[6px] text-[13px] text-[color:var(--muted)] hover:text-[color:var(--fg)] hover:bg-[color:var(--cobalt-glow)]"
          >
            <Plus className="h-4 w-4" />
            New task
          </button>
        </nav>

        <div className="px-4 py-3 border-t border-[color:var(--line)] md:hidden">
          <BudgetChip className="w-full justify-between" />
        </div>

        <div className="px-4 py-4 border-t border-[color:var(--line)] space-y-1.5 font-mono text-[10.5px]">
          <RailStat label="Agent service" value={anyOnline ? "online" : "offline"}
                    color={anyOnline ? "var(--ok)" : "var(--off)"} dot testId="rail-agent-service" />
          <RailStat label="Emergent rate"
                    value={rate != null ? `$${Number(rate).toFixed(3)}/ECU` : "—"}
                    color="var(--muted)" testId="rail-rate" />
          <RailStat label="Publish lock"
                    value={pub.held ? (pub.holder?.holder_agent || "held") : "free"}
                    color={pub.held ? "var(--warn)" : "var(--muted)"}
                    testId="rail-publish-lock" />
          <button onClick={async () => { await logout(); navigate("/login"); }}
                  data-testid="logout-btn"
                  className="w-full mt-3 inline-flex items-center gap-2 text-[color:var(--muted)] hover:text-[color:var(--fg)]">
            <LogOut className="h-3.5 w-3.5" />
            <span>Sign out</span>
          </button>
          <div className="pt-1 text-[9.5px] text-[color:var(--dim)] truncate" title={user?.email}>
            {user?.email}
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <div className="hidden md:flex justify-end px-6 py-2 border-b border-[color:var(--line)]">
          <BudgetChip />
        </div>
        {children}
      </main>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, testId, count }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={testId}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-2.5 py-2 rounded-[6px] text-[13px] transition-colors ${
          isActive
            ? "text-[color:var(--fg)]"
            : "text-[color:var(--muted)] hover:text-[color:var(--fg)] hover:bg-[color:var(--cobalt-glow)]"
        }`
      }
      style={({ isActive }) =>
        isActive
          ? { background: "var(--cobalt-glow)", border: "1px solid rgba(74,118,255,0.35)" }
          : { border: "1px solid transparent" }
      }
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1">{label}</span>
      {count > 0 && (
        <span
          className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 text-[10px] font-mono rounded-full text-white tabular-nums"
          style={{ background: "var(--cobalt)" }}
          data-testid={`${testId}-badge`}
        >
          {count}
        </span>
      )}
    </NavLink>
  );
}

function RailStat({ label, value, color, dot, testId }) {
  return (
    <div className="flex items-center justify-between gap-2" data-testid={testId}>
      <span className="text-[color:var(--muted)] flex items-center gap-1.5">
        {dot && <span className="w-[6px] h-[6px] rounded-full" style={{ background: color }} />}
        {label}
      </span>
      <span style={{ color }}>{value}</span>
    </div>
  );
}

